import { faExclamationCircle, faExclamationTriangle, faLock, faShippingFast } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'devextreme-react';
import Chart, { ArgumentAxis, Legend, Series, Title, ValueAxis } from 'devextreme-react/chart';
import DateRangeBox from 'devextreme-react/date-range-box';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { Box, CheckCircle } from 'react-feather';
import { Card, Col, Row } from 'reactstrap';
import {
    getAllShippableVehicles,
    getBlockedVehicles,
    getTotalStockOfEachWarehouse,
    getTotalStockWithInvoicedOrShippingOrders,
    getWarehouseAndDealerExitReport
} from '../../services/apis';
import useFormatMessage from '../../utils/useFormatMessage';

export default function Dashboard() {
    const mountedRef = useRef(true);
    const [totalStockLoader, setTotalStockLoader] = useState(true)
    const [invoicedStockLoader, setInvoicedStockLoader] = useState(true)
    const [blockedVehiclesLoader, setBlockedVehiclesLoader] = useState(true)
    const [chartLoader, setChartLoader] = useState(true)
    const [warehouseStocks, setWarehouseStocks] = useState([])
    const [totalStock, setTotalStock] = useState("-")
    const [invoicedStock, setInvoicedStock] = useState("-")
    const [blockedVehicles, setBlockedVehicles] = useState("-")
    const [shippableVehicles, setShippableVehicles] = useState("-")
    const [chartData, setChartData] = useState([])
    const [dateRange, setDateRange] = useState([
        new Date(new Date().setDate(new Date().getDate() - 7)), new Date()
    ])
    const [hasError, setHasError] = useState(false)
    const [totalStockError, setTotalStockError] = useState(false)
    const [invoicedStockError, setInvoicedStockError] = useState(false)
    const [blockedVehiclesError, setBlockedVehiclesError] = useState(false)
    const [shippableVehiclesError, setShippableVehiclesError] = useState(false)
    const chartRef = React.useRef(null);
    const { t } = useFormatMessage()


    let today = new Date();
    let dayOfWeek = today.getDay();
    let diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    let monday = new Date(today.setDate(today.getDate() + diffToMonday));
    let initialValue = [monday, new Date()];

    const fetchMainData = async () => {
        setHasError(false);

        // Reset error states
        setTotalStockError(false);
        setInvoicedStockError(false);
        setBlockedVehiclesError(false);
        setShippableVehiclesError(false);

        // Reset states individually
        setTotalStockLoader(true);
        setInvoicedStockLoader(true);
        setBlockedVehiclesLoader(true);
        setTotalStock("-");
        setInvoicedStock("-");
        setBlockedVehicles("-");
        setShippableVehicles("-");
        setWarehouseStocks([]);

        // Fetch total stock data
        getTotalStockOfEachWarehouse()
            .then(stockData => {
                if (!mountedRef.current) return;
                if (Array.isArray(stockData)) {
                    setWarehouseStocks(stockData);
                    const total = stockData.reduce((sum, warehouse) => sum + (warehouse.total_stock || 0), 0);
                    setTotalStock(total || "-");
                }
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setTotalStock("-");
                setTotalStockError(true);
                setHasError(true);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setTotalStockLoader(false);
            });

        // Fetch invoiced stock data
        getTotalStockWithInvoicedOrShippingOrders()
            .then(invoicedStockData => {
                if (!mountedRef.current) return;
                setInvoicedStock(invoicedStockData)
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setInvoicedStock("-");
                setInvoicedStockError(true);
                setHasError(true);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setInvoicedStockLoader(false);
            });

        // Fetch blocked vehicles data
        getBlockedVehicles()
            .then(blockedData => {
                if (!mountedRef.current) return;
                setBlockedVehicles(blockedData)
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setBlockedVehicles("-");
                setBlockedVehiclesError(true);
                setHasError(true);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setBlockedVehiclesLoader(false);
            });

        // Fetch shippable vehicles data
        getAllShippableVehicles()
            .then(shippableData => {
                if (!mountedRef.current) return;
                setShippableVehicles(shippableData)
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setShippableVehicles("-");
                setShippableVehiclesError(true);
                setHasError(true);
            });
    };

    const fetchChartData = async () => {
        setChartLoader(true);
        setChartData([]);
        setHasError(false);

        try {
            const exitReportData = await getWarehouseAndDealerExitReport(
                moment(dateRange[0]).format("YYYY-MM-DD"),
                moment(dateRange[1]).format("YYYY-MM-DD")
            ).catch(() => {
                if (!mountedRef.current) return [];
                setHasError(true);
                return [];
            });

            if (!mountedRef.current) return;

            // Transform exit report data for the chart
            const transformedData = (exitReportData || []).map(item => ({
                date: item.door_exit_date,
                bayiSevk: item.client_transfer_count || 0,
                transfer: item.warehouse_transfer_count || 0
            }));

            if (!mountedRef.current) return;
            setChartData(transformedData);
        } catch (error) {
            if (!mountedRef.current) return;
            console.error('Error fetching chart data:', error);
            setChartData([]);
            setHasError(true);
        } finally {
            if (!mountedRef.current) return;
            setChartLoader(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!mountedRef.current) return;

            // Ana sayfa yetkisi varsa dashboard verilerini çek
            if (true) {
                await fetchMainData();
                await fetchChartData();
            }
        };

        fetchData();

        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (mountedRef.current) {
            fetchChartData();
        }
    }, [dateRange]);

    const handleRefresh = () => {
        // Ana sayfa yetkisi varsa dashboard verilerini yenile
        if (true) {
            fetchMainData();
            fetchChartData();
        }
    };

    const handleDateRangeChange = (e) => {
        if (e && e.value && Array.isArray(e.value)) {
            const [startDate, endDate] = e.value;
            if (startDate && endDate) {
                setDateRange([
                    moment(startDate).startOf('day').toDate(),
                    moment(endDate).endOf('day').toDate()
                ]);
            }
        }
    };

    const MinimalLoader = () => (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px'
        }}>
            <style>
                {`
                @keyframes blink {
                    0% { opacity: 0.3; }
                    20% { opacity: 1; }
                    100% { opacity: 0.3; }
                }
            `}
            </style>
            <span style={{
                width: '4px',
                height: '4px',
                backgroundColor: '#4A6DF2',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'blink 1.4s infinite linear both',
                animationDelay: '0s'
            }}></span>
            <span style={{
                width: '4px',
                height: '4px',
                backgroundColor: '#4A6DF2',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'blink 1.4s infinite linear both',
                animationDelay: '0.2s'
            }}></span>
            <span style={{
                width: '4px',
                height: '4px',
                backgroundColor: '#4A6DF2',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'blink 1.4s infinite linear both',
                animationDelay: '0.4s'
            }}></span>
        </div>
    );

    const ChartLoader = () => (
        <div style={{
            display: 'flex',
            justifyContent: "center",
            alignItems: "center",
            height: "320px"
        }}>
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
            }}>
                <span style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#4A6DF2',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'blink 1.4s infinite linear both',
                    animationDelay: '0s'
                }}></span>
                <span style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#4A6DF2',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'blink 1.4s infinite linear both',
                    animationDelay: '0.2s'
                }}></span>
                <span style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#4A6DF2',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'blink 1.4s infinite linear both',
                    animationDelay: '0.4s'
                }}></span>
            </div>
        </div>
    );

    const ErrorIndicator = () => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#DC2626',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: '#FEE2E2'
        }}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            <span>{t("Veri alınamadı")}</span>
        </div>
    );

    return (
        <>
            <Row>
                <Col sm="6">
                    {/* <Breadcrumb current="UK" /> */}
                </Col>
                <Col sm="6" style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "10px"
                }}>
                    {hasError && <ErrorIndicator />}
                    <Button style={{ marginLeft: "0px" }} onClick={handleRefresh}>
                        <i className="fa fa-refresh" style={{ fontSize: "18px", cursor: "pointer" }}></i>
                    </Button>
                </Col>
            </Row>

            {/* İlk Satır - 4 Ana Kart */}
            <Row className="mb-2">
                <Col xl="3" sm="6">
                    <div className="card " style={{ height: "90%" }}>
                        <div style={{ padding: "20px 25px" }} className="card-body ecommerce-icons text-center">
                            <Box style={{ fontSize: "24px", marginBottom: "12px", color: "#4A6DF2" }} />
                            <div style={{ marginBottom: "10px" }}>
                                <span style={{
                                    fontSize: "16px",
                                    color: "#2d3748",
                                    fontWeight: "500",
                                    display: "block",
                                    lineHeight: "1.4"
                                }}>{t("Toplam Stok")}</span>
                            </div>
                            <h3 className="font-primary mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                                {totalStockLoader ? (
                                    <MinimalLoader />
                                ) : totalStockError ? (
                                    <ErrorIndicator />
                                ) : (
                                    <CountUp className="counter" end={totalStock} separator="." decimal="," />
                                )}
                            </h3>
                        </div>
                    </div>
                </Col>

                <Col xl="3" sm="6">
                    <div className="card " style={{ height: "90%" }}>
                        <div style={{ padding: "20px 25px" }} className="card-body ecommerce-icons text-center">
                            <CheckCircle style={{ fontSize: "24px", marginBottom: "12px", color: "#4A6DF2" }} />
                            <div style={{ marginBottom: "10px" }}>
                                <span style={{
                                    fontSize: "16px",
                                    color: "#2d3748",
                                    fontWeight: "500",
                                    display: "block",
                                    lineHeight: "1.4"
                                }}>
                                    {t("Faturalanmış")} /
                                    <br />
                                    {t("Sevk Emri Olan")}
                                </span>
                            </div>
                            <h3 className="font-primary mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                                {invoicedStockLoader ? (
                                    <MinimalLoader />
                                ) : invoicedStockError ? (
                                    <ErrorIndicator />
                                ) : (
                                    <CountUp className="counter" end={parseInt(invoicedStock) || 0} separator="." decimal="," />
                                )}
                            </h3>
                        </div>
                    </div>
                </Col>

                <Col xl="3" sm="6">
                    <div className="card " style={{ height: "90%" }}>
                        <div style={{ padding: "20px 25px" }} className="card-body ecommerce-icons text-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: "24px", marginBottom: "12px", color: "#4A6DF2" }} />
                            <div style={{ marginBottom: "10px" }}>
                                <span style={{
                                    fontSize: "16px",
                                    color: "#2d3748",
                                    fontWeight: "500",
                                    display: "block",
                                    lineHeight: "1.4"
                                }}>
                                    {t("Faturalanmamış")} /
                                    <br />
                                    {t("Sevk Emri Olmayan")}
                                </span>
                            </div>
                            <h3 className="font-primary mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                                {invoicedStockLoader || totalStockLoader ? (
                                    <MinimalLoader />
                                ) : invoicedStockError || totalStockError ? (
                                    <ErrorIndicator />
                                ) : (
                                    <CountUp className="counter" end={totalStock - parseInt(invoicedStock)} separator="." decimal="," />
                                )}
                            </h3>
                        </div>
                    </div>
                </Col>

                <Col xl="3" sm="6">
                    <div className="card " style={{ height: "90%" }}>
                        <div style={{ padding: "20px 25px" }} className="card-body ecommerce-icons text-center">
                            <FontAwesomeIcon icon={faLock} style={{ fontSize: "24px", marginBottom: "12px", color: "#4A6DF2" }} />
                            <div style={{ marginBottom: "10px" }}>
                                <span style={{
                                    fontSize: "16px",
                                    color: "#2d3748",
                                    fontWeight: "500",
                                    display: "block",
                                    lineHeight: "1.4"
                                }}>{t("Blokajlı")}</span>
                            </div>
                            <h3 className="font-primary mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                                {blockedVehiclesLoader ? (
                                    <MinimalLoader />
                                ) : blockedVehiclesError ? (
                                    <ErrorIndicator />
                                ) : (
                                    <CountUp className="counter" end={parseInt(blockedVehicles)} separator="." decimal="," />
                                )}
                            </h3>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* İkinci Satır - Grafik ve Sevk Edilebilir */}
            <Row className="mb-4">
                <Col lg="8">
                    <Card className='chart-card' style={{
                        overflow: "hidden",
                        height: "85%"
                    }}>
                        <div className="chart-header" style={{
                            padding: "16px",
                            backgroundColor: "#fff",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid #edf2f7"
                        }}>
                            <div>
                                <span style={{ fontSize: "15px", fontWeight: "600" }}>{t("Depo Çıkış Adetleri")}</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'end',
                                background: "transparent",
                                padding: "6px",
                                borderRadius: "8px"
                            }}>
                                <DateRangeBox
                                    value={dateRange}
                                    width={"300px"}
                                    displayFormat={"dd/MM/yyyy"}
                                    endDateLabel='Bitiş Tarihi'
                                    startDateLabel='Başlangıç Tarihi'
                                    onValueChanged={handleDateRangeChange}
                                    stylingMode="outlined"
                                    dropDownButtonTemplate={() => null}
                                    showClearButton={false}
                                />
                                <Button
                                    onClick={fetchChartData}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        minWidth: 'unset',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: "#4A6DF2",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "6px",
                                        marginLeft: "8px"
                                    }}
                                    hoverStateEnabled={true}
                                    activeStateEnabled={true}
                                    focusStateEnabled={false}
                                >
                                    <i className="fa fa-search" style={{ fontSize: "12px" }}></i>
                                </Button>
                                <Button
                                    onClick={() => {
                                        if (chartRef.current?.instance?.resetVisualRange) {
                                            chartRef.current.instance.resetVisualRange();
                                        }
                                    }}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        padding: 0,
                                        minWidth: 'unset',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: "#2DA7EC",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "6px",
                                        marginLeft: "8px"
                                    }}
                                    hint={t("Zoom'u Sıfırla")}
                                    hoverStateEnabled={true}
                                    activeStateEnabled={true}
                                    focusStateEnabled={false}
                                >
                                    <i className="fa fa-arrows-alt" style={{ fontSize: "12px" }}></i>
                                </Button>
                            </div>
                        </div>

                        <div className="chart-content" style={{
                            padding: "20px",
                            backgroundColor: "#fff",
                            height: "500px"
                        }}>
                            {chartLoader ? (
                                <ChartLoader />
                            ) : (
                                <Chart
                                    ref={chartRef}
                                    dataSource={chartData}
                                    style={{ height: "100%" }}
                                    zoomAndPan={{
                                        valueAxis: "both",
                                        argumentAxis: "both",
                                        dragToZoom: true,
                                        allowMouseWheel: true,
                                        panKey: "shift"
                                    }}
                                >
                                    <ArgumentAxis
                                        label={{
                                            rotationAngle: -45,
                                            overlappingBehavior: "rotate",
                                            customizeText: ({ value }) => {
                                                const date = new Date(value);
                                                return new Intl.DateTimeFormat('tr-TR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric'
                                                }).format(date);
                                            },
                                            font: {
                                                size: 11,
                                                color: "#313131"
                                            },
                                            wordWrap: "none",
                                            displayMode: "rotate"
                                        }}
                                        tickInterval="day"
                                    >
                                        <Title text="" />
                                    </ArgumentAxis>
                                    <ValueAxis
                                        label={{
                                            font: {
                                                size: 11,
                                                color: "#313131"
                                            }
                                        }}
                                    >
                                        <Title text="" />
                                    </ValueAxis>
                                    <Series
                                        valueField="bayiSevk"
                                        argumentField="date"
                                        name={t("Bayi Sevk")}
                                        type="spline"
                                        width={3}
                                        color="#4A6DF2"
                                        label={{
                                            visible: true,
                                            customizeText: ({ value }) => `${value}`,
                                            position: 'top',
                                            offset: -8,
                                            backgroundColor: "transparent",
                                            font: {
                                                color: "#4A6DF2",
                                                weight: 700,
                                                size: 17
                                            },
                                            padding: 5
                                        }}
                                        point={{
                                            visible: true,
                                            size: 8,
                                            color: "#4A6DF2",
                                            border: {
                                                color: "#ffffff",
                                                width: 2
                                            },
                                            hoverStyle: {
                                                size: 10,
                                                border: {
                                                    color: "#4A6DF2",
                                                    width: 2
                                                }
                                            }
                                        }}
                                    />
                                    <Series
                                        valueField="transfer"
                                        argumentField="date"
                                        name={t("Depolar Arası Transfer")}
                                        type="spline"
                                        width={3}
                                        color="#FF6B6B"
                                        label={{
                                            visible: true,
                                            customizeText: ({ value }) => `${value}`,
                                            position: 'bottom',
                                            offset: -8,
                                            backgroundColor: "transparent",
                                            font: {
                                                color: "#FF6B6B",
                                                weight: 700,
                                                size: 17
                                            },
                                            padding: 5
                                        }}
                                        point={{
                                            visible: true,
                                            size: 8,
                                            color: "#FF6B6B",
                                            border: {
                                                color: "#ffffff",
                                                width: 2
                                            },
                                            hoverStyle: {
                                                size: 10,
                                                border: {
                                                    color: "#FF6B6B",
                                                    width: 2
                                                }
                                            }
                                        }}
                                    />
                                    <Legend
                                        verticalAlignment="bottom"
                                        horizontalAlignment="center"
                                        itemTextPosition="right"
                                        markerSize={12}
                                        border={{
                                            visible: true,
                                            color: "#edf2f7",
                                            cornerRadius: 6
                                        }}
                                        paddingLeftRight={20}
                                        paddingTopBottom={10}
                                        columnCount={2}
                                        font={{
                                            size: 13,
                                            color: "#2d3748",
                                            weight: 500
                                        }}
                                        backgroundColor="rgba(255, 255, 255, 0.8)"
                                    />
                                </Chart>
                            )}
                        </div>
                    </Card>
                </Col>

                <Col lg="4">
                    <div className="card mb-4">
                        <div className="card-body ecommerce-icons text-center" style={{
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-around"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px"
                            }}>
                                <FontAwesomeIcon icon={faShippingFast} style={{ fontSize: "24px", color: "#4A6DF2" }} />
                                <span style={{
                                    fontSize: "16px",
                                    color: "#2d3748",
                                    fontWeight: "500",
                                    lineHeight: "1.4",
                                }}>{t("Sevk Edilebilir Araç Sayısı")}</span>
                            </div>
                            <h3 className="font-primary mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                                {shippableVehicles === "-" ? (
                                    <MinimalLoader />
                                ) : shippableVehiclesError ? (
                                    <ErrorIndicator />
                                ) : (
                                    <CountUp className="counter" end={parseInt(shippableVehicles)} separator="." decimal="," />
                                )}
                            </h3>
                        </div>
                    </div>

                    <Card>
                        <div className="card-body" style={{ padding: "16px" }}>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: "14px" }}>{t("Depo")}</th>
                                            <th style={{ fontSize: "14px" }}>{t("Toplam Stok")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {warehouseStocks.length === 0 ? (
                                            <tr>
                                                <td colSpan="2" className="text-center">
                                                    <MinimalLoader />
                                                </td>
                                            </tr>
                                        ) : (
                                            warehouseStocks.map((warehouse, index) => (
                                                <tr key={index}>
                                                    <td style={{ fontSize: "14px" }}>{warehouse.warehouse_name.replace("TIR", "TAŞIMA DURUMUNDA").trim()}</td>
                                                    <td className='font-primary' style={{
                                                        fontWeight: "600",
                                                        fontSize: "14px"
                                                    }}>
                                                        {warehouse.warehouse_name.toLowerCase().includes('tır')
                                                            ? t("Taşıma Durumunda")
                                                            : warehouse.total_stock
                                                        }
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </>
    )
} 